import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMissedIncomeDetail } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan   = '#00e5ff';
const amber  = '#ffaa44';
const green  = '#66ff99';
const red    = '#ff6666';
const purple = '#cc66ff';
const blue   = '#66aaff';

const SummaryChip = ({ label, value, color }) => (
  <div style={{ background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 10, padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span style={{ color: '#666', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
    <span style={{ color, fontSize: 18, fontWeight: 800, fontFamily: 'monospace' }}>${value.toFixed(2)}</span>
  </div>
);

const Section = ({ title, color, children, count }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div style={{ color, fontSize: 14, fontWeight: 700 }}>{title}</div>
      {count !== undefined && (
        <span style={{ background: `${color}18`, color, border: `1px solid ${color}35`, borderRadius: 12, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>
          {count}
        </span>
      )}
    </div>
    {children}
  </div>
);

const EmptyState = ({ msg }) => (
  <div style={{ background: '#111', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '20px', color: '#444', fontSize: 13, textAlign: 'center' }}>
    {msg}
  </div>
);

const REASON_LABELS = {
  no_slot:              'Didn\'t own the slot',
  insufficient_directs: 'Insufficient direct referrals',
};

const AdminMissedIncomeDetail = () => {
  const { userId } = useParams();
  const navigate   = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [activeTab, setActiveTab] = useState('generation');

  useEffect(() => {
    setLoading(true);
    setError('');
    getMissedIncomeDetail(userId)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to compute missed income'))
      .finally(() => setLoading(false));
  }, [userId]);

  const th = { color: cyan, fontSize: 11, fontWeight: 600, padding: '10px 14px', textAlign: 'left', whiteSpace: 'nowrap', background: 'rgba(0,229,255,0.06)', borderBottom: '1px solid rgba(0,229,255,0.1)' };
  const td = (color = '#aaa') => ({ color, fontSize: 12, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap' });

  const TABS = [
    { key: 'generation',  label: 'Generation',  color: blue },
    { key: 'fastrack',    label: 'Fastrack Held', color: purple },
    { key: 'milestone',   label: 'Milestone',   color: amber },
    { key: 'performance', label: 'Perf. Gap',   color: green },
  ];

  return (
    <div>
      {/* Back button */}
      <button
        onClick={() => navigate('/panel/missed-income')}
        style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: 0 }}
      >
        ← Back to users
      </button>

      {loading && (
        <div style={{ color: '#555', textAlign: 'center', padding: 60, fontSize: 14 }}>
          Computing missed income — this may take a few seconds…
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(200,50,50,0.1)', border: '1px solid rgba(200,50,50,0.3)', borderRadius: 10, padding: 16, color: red, fontSize: 13 }}>
          {error}
        </div>
      )}

      {!loading && !error && data && (() => {
        const { user, generation = [], fastrack = [], milestone = [], performanceGaps = [], summary } = data;

        return (
          <>
            {/* User header */}
            <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>{user.name}</div>
                <div style={{ color: '#aaa', fontSize: 12, marginTop: 2 }}>
                  {user.username ? `@${user.username}` : ''}&nbsp;
                  <span style={{ color: cyan, fontFamily: 'monospace' }}>{user.userCode}</span>
                </div>
                <div style={{ color: '#555', fontSize: 11, marginTop: 2 }}>{user.email}</div>
              </div>
              <div>
                <div style={{ color: '#555', fontSize: 10 }}>Active Slot</div>
                <div style={{ color: green, fontSize: 13, fontWeight: 600 }}>{user.planId?.planName || '—'}</div>
              </div>
              <div>
                <div style={{ color: '#555', fontSize: 10 }}>Joined</div>
                <div style={{ color: '#888', fontSize: 12 }}>{fmtIST(user.createdAt)}</div>
              </div>
            </div>

            {/* Summary chips */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
              <SummaryChip label="Total Missed"    value={summary.grandTotal}      color={red} />
              <SummaryChip label="Generation"      value={summary.generationTotal} color={blue} />
              <SummaryChip label="Fastrack (Held)" value={summary.fastrackTotal}   color={purple} />
              <SummaryChip label="Milestone"       value={summary.milestoneTotal}  color={amber} />
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {TABS.map(({ key, label, color }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    background: activeTab === key ? color : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${activeTab === key ? color : 'rgba(255,255,255,0.08)'}`,
                    color: activeTab === key ? '#000' : '#888',
                    borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* ── Generation Bonus Tab ── */}
            {activeTab === 'generation' && (
              <Section title="Generation Bonus Missed" color={blue} count={generation.length}>
                {generation.length === 0 ? (
                  <EmptyState msg="No missed generation bonuses found." />
                ) : (
                  <div style={{ background: '#111', border: '1px solid rgba(102,170,255,0.15)', borderRadius: 12, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                      <thead>
                        <tr>
                          {['Level', 'Reason', 'Trigger User', 'Slot', 'Directs (have/need)', 'Missed $', 'Date'].map((h) => (
                            <th key={h} style={th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {generation.map((r, i) => (
                          <tr key={i}>
                            <td style={td(blue)}>L{r.level}</td>
                            <td style={td()}>
                              <span style={{ background: r.reason === 'no_slot' ? 'rgba(255,102,102,0.12)' : 'rgba(255,170,68,0.12)', color: r.reason === 'no_slot' ? red : amber, borderRadius: 5, padding: '2px 7px', fontSize: 10, fontWeight: 600 }}>
                                {REASON_LABELS[r.reason] || r.reason}
                              </span>
                            </td>
                            <td style={td('#fff')}>
                              <div>{r.triggerUser.name}</div>
                              <div style={{ color: cyan, fontSize: 10, fontFamily: 'monospace' }}>{r.triggerUser.userCode}</div>
                            </td>
                            <td style={td(green)}>{r.plan.name}</td>
                            <td style={td()}>
                              {r.directsWithSlot !== undefined
                                ? <span style={{ color: r.directsWithSlot >= r.requiredDirects ? green : red }}>{r.directsWithSlot} / {r.requiredDirects}</span>
                                : <span style={{ color: '#555' }}>—</span>}
                            </td>
                            <td style={td(red)}>
                              <span style={{ fontWeight: 700 }}>${r.missedAmount.toFixed(2)}</span>
                            </td>
                            <td style={td('#555')}>{fmtIST(r.date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {/* Generation total row */}
                    <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(102,170,255,0.15)', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                      <span style={{ color: '#555', fontSize: 12 }}>Total missed:</span>
                      <span style={{ color: red, fontWeight: 800, fontSize: 15, fontFamily: 'monospace' }}>
                        ${generation.reduce((s, r) => s + r.missedAmount, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* ── Fastrack Held Tab ── */}
            {activeTab === 'fastrack' && (
              <Section title="Fastrack Bonuses Held" color={purple} count={fastrack.length}>
                {fastrack.length === 0 ? (
                  <EmptyState msg="No held fastrack bonuses." />
                ) : (
                  <div style={{ background: '#111', border: '1px solid rgba(204,102,255,0.15)', borderRadius: 12, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 540 }}>
                      <thead>
                        <tr>
                          {['Slot', 'Batch #', 'Cycle #', 'Bonus Held', 'Directs Needed to Unlock', 'Completed At'].map((h) => (
                            <th key={h} style={{ ...th, background: 'rgba(204,102,255,0.07)', color: purple }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {fastrack.map((r, i) => (
                          <tr key={i}>
                            <td style={td(green)}>{r.planName}</td>
                            <td style={td(purple)}>Batch {r.batchNumber}</td>
                            <td style={td()}>{r.cycleNumber ?? '—'}</td>
                            <td style={td(amber)}><span style={{ fontWeight: 700 }}>${r.bonusAmount.toFixed(2)}</span></td>
                            <td style={td()}>
                              <span style={{ color: amber }}>≥ {r.requiredDirects} direct referral{r.requiredDirects !== 1 ? 's' : ''} in {r.planName}</span>
                            </td>
                            <td style={td('#555')}>{r.completedAt ? fmtIST(r.completedAt) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(204,102,255,0.15)', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                      <span style={{ color: '#555', fontSize: 12 }}>Total held:</span>
                      <span style={{ color: amber, fontWeight: 800, fontSize: 15, fontFamily: 'monospace' }}>
                        ${fastrack.reduce((s, r) => s + r.bonusAmount, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* ── Milestone Penalty Tab ── */}
            {activeTab === 'milestone' && (
              <Section title="Milestone Reward Penalty (achieved after 90 days)" color={amber} count={milestone.length}>
                {milestone.length === 0 ? (
                  <EmptyState msg="No milestone penalties — user either achieved all ranks within 90 days or hasn't reached any milestone ranks." />
                ) : (
                  <div style={{ background: '#111', border: '1px solid rgba(255,170,68,0.15)', borderRadius: 12, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                      <thead>
                        <tr>
                          {['Rank', 'Slot', 'Earned (50%)', 'Missed (50%)', 'Date'].map((h) => (
                            <th key={h} style={{ ...th, background: 'rgba(255,170,68,0.07)', color: amber }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {milestone.map((r, i) => (
                          <tr key={i}>
                            <td style={td('#ffaa44')}><span style={{ fontWeight: 700 }}>{r.rank}</span></td>
                            <td style={td(green)}>{r.planName}</td>
                            <td style={td(green)}>${r.earned.toFixed(2)}</td>
                            <td style={td(red)}><span style={{ fontWeight: 700 }}>${r.missed.toFixed(2)}</span></td>
                            <td style={td('#555')}>{fmtIST(r.date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(255,170,68,0.15)', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                      <span style={{ color: '#555', fontSize: 12 }}>Total penalty:</span>
                      <span style={{ color: red, fontWeight: 800, fontSize: 15, fontFamily: 'monospace' }}>
                        ${milestone.reduce((s, r) => s + r.missed, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* ── Performance Gap Tab ── */}
            {activeTab === 'performance' && (
              <Section title="Performance Rank Gaps" color={green} count={performanceGaps.length}>
                {performanceGaps.length === 0 ? (
                  <EmptyState msg="No performance rank gaps found — user has achieved all applicable ranks or has no relevant slot activity." />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {performanceGaps.map((g, i) => {
                      const pct = Math.min(100, Math.round((g.directsWithSlot / g.needed) * 100));
                      return (
                        <div key={i} style={{ background: '#111', border: '1px solid rgba(102,255,153,0.12)', borderRadius: 12, padding: '16px 20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                            <div>
                              <span style={{ color: green, fontWeight: 700, fontSize: 14 }}>{g.plan}</span>
                              {!g.userOwnsSlot && (
                                <span style={{ marginLeft: 8, background: 'rgba(255,102,102,0.12)', color: red, border: '1px solid rgba(255,102,102,0.3)', borderRadius: 5, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>
                                  Doesn't own slot
                                </span>
                              )}
                            </div>
                            <div style={{ color: g.shortfall === 0 ? green : amber, fontSize: 13, fontWeight: 700 }}>
                              {g.directsWithSlot} / {g.needed} directs
                            </div>
                          </div>
                          {/* Progress bar */}
                          <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, background: pct === 100 ? green : amber, height: '100%', borderRadius: 6, transition: 'width 0.4s ease' }} />
                          </div>
                          <div style={{ marginTop: 8, color: '#555', fontSize: 11 }}>
                            {g.shortfall > 0
                              ? `Needs ${g.shortfall} more direct referral${g.shortfall !== 1 ? 's' : ''} in ${g.plan} to qualify for Performance Rank`
                              : `Meets direct count — check slot ownership to qualify`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default AdminMissedIncomeDetail;
